import { adminApiClient } from '@/services/admin/baseService'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import { evmOperationService } from '@/services/admin/evmOperationService'
import { tronOperationService } from '@/services/admin/tronOperationService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type {
  ApiResponse,
  FeeTreasuryBatchWithdrawPreviewResponse,
  FeeTreasuryCollectorStatusResponse,
  FeeTreasuryDiscrepancyListResponse,
  FeeTreasuryDiscrepancyResponse,
  FeeTreasuryBatchWithdrawExecutionResponse,
  FeeTreasuryOverview,
  FeeTreasurySummaryResponse,
  FeeTreasuryTokenStats,
  FeeTreasuryTokensResponse,
  FeeTreasuryWriteCapabilitiesResponse,
  FeeTreasuryWithdrawPreviewResponse,
  FeeTreasuryWithdrawExecutionResponse,
  FeeTreasuryWhitelistCountResponse,
  FeeTreasuryWhitelistResponse,
  FeeTreasuryWhitelistStatusResponse,
  FeeTreasuryWithdrawableResponse,
  EvmOperationEnvelope,
  EvmPendingOperationItem,
  TronOperationEnvelope,
  TronPendingOperationItem,
} from '@/types'

export const feeTreasuryService = {
  createEvmWithdrawOperation(
    adminId: string,
    chainId: string,
    chainRegistryId: string,
    token: string,
    recipientAddress: string,
    amount: string,
    auditReason: string,
    mode: NetworkEnvironment
  ): EvmPendingOperationItem {
    return evmOperationService.createPending(adminId, mode, {
      operationType: 'evm_fee_treasury_withdraw',
      requestBody: { recipientAddress, amount, auditReason },
      clientResourceLocator: { chainId, token },
      resourceType: 'fee_treasury',
      resourceId: chainRegistryId,
    })
  },

  createEvmBatchWithdrawOperation(
    adminId: string,
    chainId: string,
    chainRegistryId: string,
    recipientAddress: string,
    withdrawals: Array<{ token: string; amount: string }>,
    auditReason: string,
    mode: NetworkEnvironment
  ): EvmPendingOperationItem {
    return evmOperationService.createPending(adminId, mode, {
      operationType: 'evm_fee_treasury_batch_withdraw',
      requestBody: { recipientAddress, withdrawals, auditReason },
      clientResourceLocator: { chainId },
      resourceType: 'fee_treasury',
      resourceId: chainRegistryId,
    })
  },

  async submitEvmOperation(
    adminId: string,
    item: EvmPendingOperationItem,
    mode: NetworkEnvironment
  ): Promise<EvmOperationEnvelope> {
    return evmOperationService.submitPending(adminId, mode, item)
  },

  createTronWithdrawOperation(
    adminId: string,
    chainId: string,
    chainRegistryId: string,
    token: string,
    recipientAddress: string,
    amount: string,
    auditReason: string,
    mode: NetworkEnvironment
  ): TronPendingOperationItem {
    return tronOperationService.createPending(adminId, mode, {
      operationType: 'tron_fee_treasury_withdraw',
      requestBody: { recipientAddress, amount, auditReason },
      clientResourceLocator: { chainId, token },
      resourceId: chainRegistryId,
    })
  },

  createTronBatchWithdrawOperation(
    adminId: string,
    chainId: string,
    chainRegistryId: string,
    recipientAddress: string,
    withdrawals: Array<{ token: string; amount: string }>,
    auditReason: string,
    mode: NetworkEnvironment
  ): TronPendingOperationItem {
    return tronOperationService.createPending(adminId, mode, {
      operationType: 'tron_fee_treasury_batch_withdraw',
      requestBody: { recipientAddress, withdrawals, auditReason },
      clientResourceLocator: { chainId },
      resourceId: chainRegistryId,
    })
  },

  async submitTronOperation(
    adminId: string,
    item: TronPendingOperationItem,
    mode: NetworkEnvironment
  ): Promise<TronOperationEnvelope> {
    return tronOperationService.submitPending(adminId, mode, item)
  },

  async getFeeTreasuryContracts(mode?: NetworkEnvironment): Promise<FeeTreasuryOverview[]> {
    const response = await adminApiClient.get<ApiResponse<{ contracts: FeeTreasuryOverview[] }>>(
      '/admin/fee-treasury',
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data?.contracts) {
      throw new Error(response.data?.error || 'Failed to load fee treasury contracts')
    }

    return response.data.data.contracts
  },

  async getFeeTreasuryTokens(
    chainId: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryTokensResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryTokensResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch fee treasury tokens')
    }

    return response.data.data
  },

  async getFeeTreasurySummary(
    chainId: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasurySummaryResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasurySummaryResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/summary`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch fee treasury summary')
    }

    return response.data.data
  },

  async getFeeTreasuryTokenStats(
    chainId: string,
    token: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryTokenStats> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryTokenStats>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens/${encodeURIComponent(token)}/stats`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch token stats')
    }

    return response.data.data
  },

  async getFeeTreasuryDiscrepancy(
    chainId: string,
    token: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryDiscrepancyResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryDiscrepancyResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens/${encodeURIComponent(token)}/discrepancy`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch balance discrepancy')
    }

    return response.data.data
  },

  async getFeeTreasuryDiscrepancies(
    chainId: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryDiscrepancyListResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryDiscrepancyListResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/discrepancies`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch discrepancy list')
    }

    return response.data.data
  },

  async checkFeeTreasuryWithdrawable(
    chainId: string,
    token: string,
    amount: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWithdrawableResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryWithdrawableResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens/${encodeURIComponent(token)}/withdrawable`,
      {
        params: { amount },
        ...buildModeAwareConfig(mode),
      }
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to evaluate withdrawable amount')
    }

    return response.data.data
  },

  async withdrawSolanaFee(
    chainId: string,
    token: string,
    recipientAddress: string,
    amount: string,
    auditReason: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWithdrawExecutionResponse> {
    const response = await adminApiClient.post<ApiResponse<FeeTreasuryWithdrawExecutionResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens/${encodeURIComponent(token)}/withdraw`,
      {
        recipientAddress,
        amount,
        auditReason,
      },
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to execute treasury withdraw')
    }

    return response.data.data
  },

  async batchWithdrawSolanaFee(
    chainId: string,
    recipientAddress: string,
    withdrawals: Array<{ token: string; amount: string }>,
    auditReason: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryBatchWithdrawExecutionResponse> {
    const response = await adminApiClient.post<ApiResponse<FeeTreasuryBatchWithdrawExecutionResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/batch-withdraw`,
      {
        recipientAddress,
        withdrawals,
        auditReason,
      },
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to execute treasury batch withdraw')
    }

    return response.data.data
  },

  async getWriteCapabilities(
    chainId: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWriteCapabilitiesResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryWriteCapabilitiesResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/capabilities`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to load treasury write capabilities')
    }

    return response.data.data
  },

  async previewWithdrawFee(
    chainId: string,
    token: string,
    recipientAddress: string,
    amount: string,
    auditReason?: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWithdrawPreviewResponse> {
    const response = await adminApiClient.post<ApiResponse<FeeTreasuryWithdrawPreviewResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens/${encodeURIComponent(token)}/withdraw-preview`,
      {
        recipientAddress,
        amount,
        ...(auditReason ? { auditReason } : {}),
      },
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to preview treasury withdraw')
    }

    return response.data.data
  },

  async previewBatchWithdrawFee(
    chainId: string,
    recipientAddress: string,
    withdrawals: Array<{ token: string; amount: string }>,
    auditReason?: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryBatchWithdrawPreviewResponse> {
    const response = await adminApiClient.post<ApiResponse<FeeTreasuryBatchWithdrawPreviewResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/batch-withdraw-preview`,
      {
        recipientAddress,
        withdrawals,
        ...(auditReason ? { auditReason } : {}),
      },
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to preview treasury batch withdraw')
    }

    return response.data.data
  },

  async getFeeTreasuryCollectorStatus(
    chainId: string,
    collectorAddress: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryCollectorStatusResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryCollectorStatusResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/collectors/${encodeURIComponent(collectorAddress)}/status`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch collector status')
    }

    return response.data.data
  },

  async getFeeTreasuryWhitelist(
    chainId: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWhitelistResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryWhitelistResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/withdrawal-whitelist`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch whitelist')
    }

    return response.data.data
  },

  async getFeeTreasuryWhitelistCount(
    chainId: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWhitelistCountResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryWhitelistCountResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/withdrawal-whitelist/count`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to fetch whitelist count')
    }

    return response.data.data
  },

  async getFeeTreasuryWhitelistStatus(
    chainId: string,
    accountAddress: string,
    mode?: NetworkEnvironment
  ): Promise<FeeTreasuryWhitelistStatusResponse> {
    const response = await adminApiClient.get<ApiResponse<FeeTreasuryWhitelistStatusResponse>>(
      `/admin/fee-treasury/${encodeURIComponent(chainId)}/withdrawal-whitelist/${encodeURIComponent(accountAddress)}`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to check whitelist status')
    }

    return response.data.data
  },
}
